// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * The index, and scoring against it.
 *
 * Every frame embedding is L2-normalised once, on the way in. That turns each
 * query into a plain dot product over one contiguous `Float32Array` — no
 * per-frame square roots, no object per frame, no allocation in the hot loop.
 * A two-hour video sampled at 1 fps is 7,200 x 512 floats = 14.7 MB, and a full
 * re-score of it is single-digit milliseconds, which is what lets results
 * update on every keystroke instead of behind a Search button.
 *
 * Brute force is not a placeholder here. An approximate index (HNSW, IVF) would
 * add a build cost and an accuracy caveat to save nothing: the whole array fits
 * in L2/L3 cache-friendly streaming and the user is going to change the query
 * far more often than they change the video.
 */

export const EMBED_DIM = 512;

export class FrameIndex {
  /** Row-major, `count * EMBED_DIM`, every row unit length. */
  readonly data: Float32Array;
  /** Seconds into the video for each row. */
  readonly times: Float64Array;
  private used = 0;

  constructor(capacity: number) {
    this.data = new Float32Array(capacity * EMBED_DIM);
    this.times = new Float64Array(capacity);
  }

  get count(): number {
    return this.used;
  }

  /** Append one frame. The vector is copied and normalised; the caller keeps ownership. */
  push(time: number, vector: Float32Array): void {
    if (vector.length !== EMBED_DIM) {
      throw new Error(`Expected a ${EMBED_DIM}-dimension embedding, got ${vector.length}.`);
    }
    if (this.used >= this.times.length) throw new Error('The frame index is full.');
    const at = this.used * EMBED_DIM;
    this.data.set(vector, at);
    normaliseInPlace(this.data, at, EMBED_DIM);
    this.times[this.used] = time;
    this.used += 1;
  }

  /**
   * Cosine of every frame against one query vector.
   *
   * Writes into `out` when given one, so a live-updating UI can re-score on
   * each keystroke without allocating a new array sixty times a second.
   */
  score(query: Float32Array, out?: Float32Array): Float32Array {
    const q = new Float32Array(query);
    normaliseInPlace(q, 0, EMBED_DIM);
    const scores = out && out.length >= this.used ? out : new Float32Array(this.used);
    const { data } = this;
    for (let i = 0; i < this.used; i++) {
      const base = i * EMBED_DIM;
      let dot = 0;
      for (let d = 0; d < EMBED_DIM; d++) dot += data[base + d]! * q[d]!;
      scores[i] = dot;
    }
    return scores.length === this.used ? scores : scores.subarray(0, this.used);
  }

  /** The rows actually filled, for export. */
  vectors(): Float32Array {
    return this.data.subarray(0, this.used * EMBED_DIM);
  }

  timeList(): number[] {
    return Array.from(this.times.subarray(0, this.used));
  }

  static from(times: readonly number[], vectors: Float32Array): FrameIndex {
    const index = new FrameIndex(times.length);
    for (let i = 0; i < times.length; i++) {
      index.push(times[i]!, vectors.subarray(i * EMBED_DIM, (i + 1) * EMBED_DIM));
    }
    return index;
  }
}

export function normaliseInPlace(buf: Float32Array, offset: number, length: number): void {
  let sum = 0;
  for (let i = 0; i < length; i++) {
    const v = buf[offset + i]!;
    sum += v * v;
  }
  // A zero vector cannot be normalised and must not become NaN — a single NaN
  // in the index would poison every comparison that touches it.
  const norm = Math.sqrt(sum);
  if (!(norm > 0)) return;
  for (let i = 0; i < length; i++) buf[offset + i] = buf[offset + i]! / norm;
}

export function cosine(a: ArrayLike<number>, b: ArrayLike<number>): number {
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i]! * b[i]!;
    na += a[i]! * a[i]!;
    nb += b[i]! * b[i]!;
  }
  const d = Math.sqrt(na) * Math.sqrt(nb);
  return d > 0 ? dot / d : 0;
}
